import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import { Calendar, Clock, Loader2, Mail, Plus, Save, X } from 'lucide-react';
import { reportService, Report, ReportSchedule } from '@/services/reportService';

interface ReportSchedulerProps {
  report: Report;
  onClose: () => void;
  onSave: () => void;
}

interface ScheduleFormData {
  isActive: boolean;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  time: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  recipients: string[];
}

const ReportScheduler: React.FC<ReportSchedulerProps> = ({ report, onClose, onSave }) => {
  const { toast } = useToast();
  const [recipientInput, setRecipientInput] = useState('');
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ScheduleFormData>({
    defaultValues: {
      isActive: report.schedule?.isActive || false,
      frequency: report.schedule?.frequency || 'DAILY',
      time: report.schedule?.time || '09:00',
      dayOfWeek: report.schedule?.dayOfWeek || 1,
      dayOfMonth: report.schedule?.dayOfMonth || 1,
      recipients: report.schedule?.recipients || []
    }
  });

  const watchedValues = watch();

  const updateScheduleMutation = useMutation({
    mutationFn: (scheduleData: ReportSchedule) => 
      reportService.updateReport(report._id, { schedule: scheduleData }),
    onSuccess: () => {
      toast({
        title: 'Schedule Updated',
        description: 'Report schedule has been updated successfully.',
      });
      onSave();
    },
    onError: (error: any) => {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update schedule.',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: ScheduleFormData) => {
    const scheduleData: ReportSchedule = {
      ...data,
      lastRunDate: report.schedule?.lastRunDate,
      nextRunDate: calculateNextRunDate(data)
    };

    updateScheduleMutation.mutate(scheduleData);
  };

  const calculateNextRunDate = (schedule: ScheduleFormData): string => {
    const now = new Date();
    const [hours, minutes] = schedule.time.split(':').map(Number);
    
    let nextRun = new Date(now);
    nextRun.setHours(hours, minutes, 0, 0);
    
    switch (schedule.frequency) {
      case 'DAILY':
        if (nextRun <= now) {
          nextRun.setDate(nextRun.getDate() + 1);
        }
        break;
      case 'WEEKLY': {
        const targetDay = schedule.dayOfWeek || 1;
        const currentDay = nextRun.getDay();
        let daysToAdd = targetDay - currentDay;
        if (daysToAdd <= 0 || (daysToAdd === 0 && nextRun <= now)) {
          daysToAdd += 7;
        }
        nextRun.setDate(nextRun.getDate() + daysToAdd);
        break;
      }
      case 'MONTHLY': {
        const targetDate = schedule.dayOfMonth || 1;
        nextRun.setDate(targetDate);
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 1);
        }
        break;
      }
      case 'QUARTERLY':
        nextRun.setMonth(nextRun.getMonth() + 3);
        break;
      case 'YEARLY':
        nextRun.setFullYear(nextRun.getFullYear() + 1);
        break;
    }
    
    return nextRun.toISOString();
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (email && isValidEmail(email) && !watchedValues.recipients.includes(email)) {
      const newRecipients = [...watchedValues.recipients, email];
      setValue('recipients', newRecipients);
      setRecipientInput('');
    }
  };

  const removeRecipient = (emailToRemove: string) => {
    const newRecipients = watchedValues.recipients.filter(email => email !== emailToRemove);
    setValue('recipients', newRecipients);
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getDayOfWeekName = (day: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day] || 'Monday';
  };

  const getFrequencyDescription = (frequency: string): string => {
    switch (frequency) {
      case 'DAILY':
        return 'Report will be generated every day';
      case 'WEEKLY':
        return 'Report will be generated once per week';
      case 'MONTHLY':
        return 'Report will be generated once per month';
      case 'QUARTERLY':
        return 'Report will be generated every 3 months';
      case 'YEARLY':
        return 'Report will be generated once per year';
      default:
        return '';
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Schedule Report: {report.name}
          </DialogTitle>
          <DialogDescription>
            Configure automated report generation and distribution
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Enable/Disable Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Schedule Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="isActive">Enable Scheduled Generation</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically generate this report on a schedule
                  </p>
                </div>
                <Switch
                  id="isActive"
                  checked={watchedValues.isActive}
                  onCheckedChange={(checked) => setValue('isActive', checked)}
                />
              </div>

              {watchedValues.isActive && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="frequency">Frequency *</Label>
                      <Select
                        value={watchedValues.frequency}
                        onValueChange={(value) => setValue('frequency', value as any)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DAILY">Daily</SelectItem>
                          <SelectItem value="WEEKLY">Weekly</SelectItem>
                          <SelectItem value="MONTHLY">Monthly</SelectItem>
                          <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                          <SelectItem value="YEARLY">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {getFrequencyDescription(watchedValues.frequency)}
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="time">Time *</Label>
                      <Input
                        id="time"
                        type="time"
                        {...register('time', { required: 'Time is required' })}
                      />
                      {errors.time && (
                        <p className="text-sm text-red-600 mt-1">{errors.time.message}</p>
                      )}
                    </div>
                  </div>

                  {watchedValues.frequency === 'WEEKLY' && (
                    <div>
                      <Label htmlFor="dayOfWeek">Day of Week</Label>
                      <Select
                        value={watchedValues.dayOfWeek?.toString() || '1'}
                        onValueChange={(value) => setValue('dayOfWeek', parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select day" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Sunday</SelectItem>
                          <SelectItem value="1">Monday</SelectItem>
                          <SelectItem value="2">Tuesday</SelectItem>
                          <SelectItem value="3">Wednesday</SelectItem>
                          <SelectItem value="4">Thursday</SelectItem>
                          <SelectItem value="5">Friday</SelectItem>
                          <SelectItem value="6">Saturday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {watchedValues.frequency === 'MONTHLY' && (
                    <div>
                      <Label htmlFor="dayOfMonth">Day of Month</Label>
                      <Select
                        value={watchedValues.dayOfMonth?.toString() || '1'}
                        onValueChange={(value) => setValue('dayOfMonth', parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select day" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                            <SelectItem key={day} value={day.toString()}>
                              {day}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Email Recipients */}
          {watchedValues.isActive && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email Recipients
                </CardTitle>
                <CardDescription>
                  Add email addresses to receive the generated reports
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email address..."
                    value={recipientInput}
                    onChange={(e) => setRecipientInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  />
                  <Button type="button" onClick={addRecipient} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                {watchedValues.recipients.length > 0 && (
                  <div className="space-y-2">
                    <Label>Recipients ({watchedValues.recipients.length})</Label>
                    <div className="flex flex-wrap gap-2">
                      {watchedValues.recipients.map((email, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1">
                          {email}
                          <button
                            type="button"
                            onClick={() => removeRecipient(email)}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {watchedValues.recipients.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No recipients added. Reports will be generated but not emailed.
                  </p>
                )}

                <div className="border-t pt-4 space-y-3">
                  <Label className="text-sm font-medium">Email Options</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Include Summary</Label>
                        <p className="text-xs text-muted-foreground">
                          Include report summary in email body
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Attach Charts</Label>
                        <p className="text-xs text-muted-foreground">
                          Include visualization charts as images
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Schedule Summary */}
          {watchedValues.isActive && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Schedule Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Frequency:</span>
                    <span className="font-medium">{watchedValues.frequency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time:</span>
                    <span className="font-medium">{watchedValues.time}</span>
                  </div>
                  {watchedValues.frequency === 'WEEKLY' && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Day:</span>
                      <span className="font-medium">{getDayOfWeekName(watchedValues.dayOfWeek || 1)}</span>
                    </div>
                  )}
                  {watchedValues.frequency === 'MONTHLY' && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Day of Month:</span>
                      <span className="font-medium">{watchedValues.dayOfMonth}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Recipients:</span>
                    <span className="font-medium">{watchedValues.recipients.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Next Run:</span>
                    <span className="font-medium">
                      {watchedValues.isActive 
                        ? new Date(calculateNextRunDate(watchedValues)).toLocaleString()
                        : 'Not scheduled'
                      }
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={updateScheduleMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateScheduleMutation.isPending}
              className="flex items-center gap-2"
            >
              {updateScheduleMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {updateScheduleMutation.isPending ? 'Saving…' : 'Save Schedule'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportScheduler;